import { useCallback, useEffect, useMemo, useRef } from "react";
import { setCourseEnded } from "store/actions/event/courses";
import { useAppDispatch, useTypedSelector } from "store/reducers/use-typed-selector";
import { sleep } from "../../../../utils/utils";
import { Session } from "types/working-model";
import { ICourse } from "types/courses";

export const checkIfCourseEnded = async (sessions: Session[]): Promise<boolean | null> => {
	const lastTimestamp = sessions.reduce((acc, session) => {
		if (!session.end_timestamp) return acc;
		return Math.max(acc, session.end_timestamp);
	}, 0);

	if (!lastTimestamp) return false;

	const now = new Date();
	const _hasCourseEnded = lastTimestamp < now.getTime();
	return _hasCourseEnded;
};

export const getSessionsWithRequirements = (course?: ICourse, sessions?: Session[]) => {
	if (!course?.requirements || !sessions?.length) {
		return [];
	}

	const sessionIds = new Set(course.requirements.map(req => req.session_id));
	return sessions.filter(session => sessionIds.has(session.session) && session.end_timestamp);
};

// course end is defined as:
// "the end time of the session with the furthest out end time amongst all sessions that have at least one requirement"
const useCheckIfCourseEnded = () => {
	const isRunningRecursiveCheck = useRef(false);

	const workingEvent = useTypedSelector((state) => state.CreateEventReducer.workingEvent);
	const eventBundle = useTypedSelector((state) => state.LiveEventReducer.eventBundle);
	const course = useTypedSelector((state) => state.CourseReducer.course);
	const hasCourseEnded = useTypedSelector((state) => state.CourseReducer.courseEnded);

	const hasCourse = !!course;

	const dispatch = useAppDispatch();

	const event = eventBundle ?? workingEvent;
	const sessions = event?.sessions;

	const memoizedGetSessionsWithRequirements = useCallback(() => {
		return getSessionsWithRequirements(course, sessions);
	}, [course, sessions]);


	useEffect(() => {
		if (isRunningRecursiveCheck.current) return;
		if (!hasCourse) {
			if (hasCourseEnded !== null) {
				dispatch(setCourseEnded(null));
			}
			return;
		}

		isRunningRecursiveCheck.current = true;

		const recursiveCheck = async () => {
			try {
				const result = await checkIfCourseEnded(memoizedGetSessionsWithRequirements());
				if (result !== hasCourseEnded) {
					dispatch(setCourseEnded(result));
					// only return early if true, otherwise keep checking
					if (result) return;
				}

				// wait 10 seconds before rechecking, no need to check too frequently
				await sleep(10000);
				recursiveCheck();
			} catch (error) {
				console.error('Error in useCheckIfCourseEnded:', error);
			}
		};

		recursiveCheck();

	}, [dispatch, memoizedGetSessionsWithRequirements, hasCourseEnded, hasCourse]);
};

export default useCheckIfCourseEnded;
